import moment from 'moment'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import { useEffect, useMemo, useState } from 'react'
import { deserialize } from '../../../../../utility/serialization'
import { displayWithCurrency } from '../../../../../utility/currency'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { COMMON_ERRORS } from '../../../../../misc/Constants'
import { DATETIME_FORMAT, getMomentInCommunityTimezone } from '../../../../../utility/datesHelper'
import { requestCommunityFinancialPerformanceReport } from '../../../../../actions/reportsActions'
import ToggleTabs from '../../../../common/ToggleTabs'
import Loader from '../../../../common/loader/Loader'
import FinancialPerformanceStackedChart from './FinancialPerformanceStackedChart'
import Placeholder from './Placeholder'
import FiltersButtonDropdown, { FIXED_DATE_RANGES_FUNCTIONS } from '../../FiltersButtonDropdown'

import classes from '../BusinessDashboard.module.scss'
import { ReactComponent as InfoCircleIcon } from '../../../../../assets/images/common/icons/info-circle-icon-dark.svg'

const SingleStatistics = ({ data, label, currency, communityHasEnrollments, noRevenueMessage }) => {
  const growthPercentage = data.prevYearValue ? ((data.value - data.prevYearValue) / data.prevYearValue) : null
  const growthPercentageString = Intl.NumberFormat('en-US', { style: 'percent', signDisplay: 'exceptZero' })
    .format(growthPercentage)
  const useDash = data.value === 0 && communityHasEnrollments

  return (
    <div className={classnames('d-flex flex-column text-center', classes.statistics)}>
      <span className={classes.value}>
        {useDash ? '-'
          : currency
            ? displayWithCurrency(data.value, currency, true)
            : data.value
        }
      </span>
      <span className={classes.topLabel}>{label}</span>
      {growthPercentage !== null && !useDash && (
        <span className={classnames(classes.bottomLabel, growthPercentage < 0 ? classes.red : classes.green )}>
          {growthPercentageString} from previous year
        </span>
      )}
      {noRevenueMessage && (
        <span className={classnames(classes.bottomLabel, classes.orange)}>
          {noRevenueMessage}
        </span>
      )}
    </div>
  )
}

SingleStatistics.propTypes = {
  data: PropTypes.shape({
    value: PropTypes.number,
    prevYearValue: PropTypes.number,
  }),
  label: PropTypes.string,
  currency: PropTypes.object,
  communityHasEnrollments: PropTypes.bool,
  noRevenueMessage: PropTypes.string,
}

const DEFAULT_FILTERS = {
  ...FIXED_DATE_RANGES_FUNCTIONS['thisYear'].valueFunc(),
  categoryOption: null,
  programOption: null,
}

const FinancialPerformanceWidget = ({ isLoadingEnrollmentsPresence, communityHasEnrollments }) => {
  const community = useSelector(state => state.user.current_user.communities[0])
  const { loadingCategories } = useSelector(state => state.dashboard)

  const [filters, setFilters] = useState(DEFAULT_FILTERS)
  const [isLoadingChartData, setIsLoadingChartData] = useState(true)
  const [data, setData] = useState()
  const noDataAvailable = useMemo(() => data?.monthlyData?.every(month => month.value === 0), [data?.monthlyData])

  useEffect(() => {
    setIsLoadingChartData(true)

    requestCommunityFinancialPerformanceReport({
      communityId: community?.id,
      startDate: getMomentInCommunityTimezone(moment(filters.startDate), community?.timezone, DATETIME_FORMAT).toDate(),
      endDate: getMomentInCommunityTimezone(moment(filters.endDate), community?.timezone, DATETIME_FORMAT).toDate(),
      courseCategoryId: filters.categoryOption?.value,
      programId: filters.programOption?.value,
      withRevenue: filters.withRevenue,
    })
      .then(res => {
        setData(deserialize(res.data.report.data))
        setIsLoadingChartData(false)
      })
      .catch(() => toast.error(COMMON_ERRORS[500]))
  }, [community, filters.categoryOption?.value, filters.endDate, filters.programOption?.value, filters.startDate, filters.withRevenue])

  const handleTwoButtonSwitcherClick = () => {
    setFilters(prevFilters => ({ ...prevFilters, withRevenue: !prevFilters.withRevenue }))
  }

  const handleSubmitFilters = (newFilters) => {
    setFilters(prevState => ({ ...prevState, ...newFilters }))
  }

  const hasDataWithoutRevenue = !data?.coursesWithPackages && data?.estTotalRevenue.value === 0 && data?.totalRegistrations.value !== 0
  const isLoading = isLoadingEnrollmentsPresence || isLoadingChartData || loadingCategories

  return (
    <section className={classes.widget}>
      <div className={classnames('d-flex justify-content-between align-items-end', classes.titleWrapper)}>
        <div className='d-flex align-items-center'>
          <h2 className={classnames('mr-3', classes.title)}>Financial Performance</h2>
          <InfoCircleIcon role='button' />
        </div>
        <FiltersButtonDropdown
          defaultFilters={DEFAULT_FILTERS}
          appliedFilters={filters}
          onSubmitFilters={handleSubmitFilters}
        />
      </div>
      <ToggleTabs
        isRightSelected={filters.withRevenue}
        setIsSelectedRight={handleTwoButtonSwitcherClick}
        buttonsText={['Registrations', 'Revenue']}
      />
      {isLoading ? (
        <div className='flex-grow-1 d-flex justify-content-center align-items-center'>
          <Loader>Loading data</Loader>
        </div>
      ) : (
        <>
          <div className={classnames('d-flex justify-content-between', classes.sectionWrapper)}>
            <SingleStatistics
              communityHasEnrollments={communityHasEnrollments}
              data={data.totalRegistrations}
              label='Total Registrations'
            />
            <SingleStatistics
              communityHasEnrollments={communityHasEnrollments}
              data={{ value: data.totalRegistrations.value === 0 ? 0 : data.estTotalRevenue.value / data.totalRegistrations.value }}
              label='Avg Price/Registration'
              currency={community?.currency}
              noRevenueMessage={hasDataWithoutRevenue ? 'To calculate average price, programs must use payment packages.' : null}
            />
            <SingleStatistics
              communityHasEnrollments={communityHasEnrollments}
              data={data.estTotalRevenue} label='Est. Total Revenue'
              currency={community?.currency}
              noRevenueMessage={hasDataWithoutRevenue ? 'To track revenue, programs must use payment packages.' : null}
            />
          </div>
          <div className={classes.chartWrapper}>
            {noDataAvailable ? (
              <Placeholder
                communityHasEnrollments={communityHasEnrollments}
                isRevenue={filters.withRevenue}
                hasDataWithoutRevenue={hasDataWithoutRevenue}
              />
            ) : (
              <FinancialPerformanceStackedChart monthlyData={data.monthlyData} isRevenue={filters.withRevenue} />
            )}
          </div>

        </>
      )}
    </section>
  )
}

FinancialPerformanceWidget.propTypes = {
  isLoadingEnrollmentsPresence: PropTypes.bool,
  communityHasEnrollments: PropTypes.bool,
}

export default FinancialPerformanceWidget
